import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { AppointmentRow } from '@/hooks/useAppointments';
import { theme } from '@/lib/theme';
import CalendarToolbar, { type CalendarViewMode } from './CalendarToolbar';
import CalendarDayView from './CalendarDayView';
import CalendarWeekView from './CalendarWeekView';
import CalendarMonthView from './CalendarMonthView';
import AppointmentDetailModal from './AppointmentDetailModal';

interface Props {
  appointments: AppointmentRow[];
  loading: boolean;
  cancelling: string | null;
  completing?: string | null;
  onCancel: (id: string) => Promise<boolean>;
  onNoShow?: (id: string) => Promise<boolean>;
  onComplete?: (id: string, method: 'cash' | 'transfer') => Promise<{ ok: boolean; error?: string }>;
  onConfirm?: (id: string) => Promise<boolean>;
}

function getWeekStart(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getMonthStart(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export default function AppointmentsCalendar({ appointments, loading, cancelling, completing, onCancel, onNoShow, onComplete, onConfirm }: Props) {
  const [viewMode, setViewMode] = useState<CalendarViewMode>('week');
  const [currentDate, setCurrentDate] = useState<Date>(() => new Date());
  const [selectedAppt, setSelectedAppt] = useState<AppointmentRow | null>(null);

  const handlePrev = useCallback(() => {
    setCurrentDate((prev) => {
      const d = new Date(prev);
      if (viewMode === 'day') d.setDate(d.getDate() - 1);
      else if (viewMode === 'week') d.setDate(d.getDate() - 7);
      else d.setMonth(d.getMonth() - 1);
      return d;
    });
  }, [viewMode]);

  const handleNext = useCallback(() => {
    setCurrentDate((prev) => {
      const d = new Date(prev);
      if (viewMode === 'day') d.setDate(d.getDate() + 1);
      else if (viewMode === 'week') d.setDate(d.getDate() + 7);
      else d.setMonth(d.getMonth() + 1);
      return d;
    });
  }, [viewMode]);

  const handleToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  const handleViewChange = useCallback((mode: CalendarViewMode) => {
    setViewMode(mode);
  }, []);

  const weekStart = getWeekStart(currentDate);
  const monthStart = getMonthStart(currentDate);

  return (
    <>
      <div
        className="rounded-xl border overflow-hidden flex flex-col"
        style={{
          backgroundColor: theme.colors.bgSurface,
          borderColor: theme.colors.border,
          minHeight: '600px',
        }}
      >
        <CalendarToolbar
          viewMode={viewMode}
          onViewChange={handleViewChange}
          currentDate={viewMode === 'week' ? weekStart : viewMode === 'month' ? monthStart : currentDate}
          onPrev={handlePrev}
          onNext={handleNext}
          onToday={handleToday}
        />

        {/* Loading overlay */}
        {loading && (
          <div className="flex-1 flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-loader-4-line text-2xl animate-spin" style={{ color: theme.colors.primary }} />
              </div>
              <p className="text-white/30 text-sm">Cargando citas…</p>
            </div>
          </div>
        )}

        {/* Calendar views */}
        {!loading && (
          <AnimatePresence mode="wait">
            <motion.div
              key={viewMode}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="flex-1 overflow-hidden"
            >
              {viewMode === 'day' && (
                <CalendarDayView
                  date={currentDate}
                  appointments={appointments}
                  onSelectAppointment={setSelectedAppt}
                />
              )}
              {viewMode === 'week' && (
                <CalendarWeekView
                  weekStart={weekStart}
                  appointments={appointments}
                  onSelectAppointment={setSelectedAppt}
                />
              )}
              {viewMode === 'month' && (
                <CalendarMonthView
                  month={monthStart}
                  appointments={appointments}
                  onSelectAppointment={setSelectedAppt}
                />
              )}
            </motion.div>
          </AnimatePresence>
        )}
      </div>

      {/* Detail modal */}
      <AppointmentDetailModal
        appointment={selectedAppt}
        onClose={() => setSelectedAppt(null)}
        onCancel={onCancel}
        onNoShow={onNoShow}
        onComplete={onComplete}
        onConfirm={onConfirm}
        cancelling={cancelling}
        completing={completing}
      />
    </>
  );
}
